import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SEO } from '../components/SEO';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ChevronDownIcon } from 'lucide-react';
const faqs = [{
  question: 'What is KittyPot?',
  answer: 'KittyPot is a shared expense and settlement tracker. It helps groups add expenses, split them fairly, and see the simplest way to settle up.'
}, {
  question: 'Does KittyPot hold my money?',
  answer: 'No. The current KittyPot MVP does not hold, move, store or manage real money. It helps groups track shared expenses and settle up outside the app.'
}, {
  question: 'Is KittyPot a wallet?',
  answer: 'Not in the current MVP. KittyPot is currently focused on shared expense tracking and manual settlement.'
}, {
  question: 'How do people pay each other?',
  answer: 'For the MVP, users pay each other outside KittyPot using bank transfer, cash or another method. KittyPot helps track what is unpaid, pending and confirmed.'
}, {
  question: 'What is a kitty?',
  answer: 'A kitty is a shared space for a group expense, trip, event or plan. Members can add expenses and see the simplest way to settle up.'
}, {
  question: 'Can I split expenses unequally?',
  answer: 'Yes. KittyPot supports equal splits and custom amounts, so you can enter exactly how much each person owes.'
}, {
  question: 'What is ‘Simplest way to settle’?',
  answer: 'It shows the fewest payments needed to make everyone even in a kitty. Instead of every person paying everyone back separately, KittyPot works out the cleanest way to settle up.'
}, {
  question: 'What is My Balance?',
  answer: 'My Balance shows what you’re owed and what you owe across all your kitties.'
}, {
  question: 'What does ‘pending confirmation’ mean?',
  answer: 'It means someone has marked a payment as paid, but the receiver has not confirmed it yet.'
}, {
  question: 'Can I use KittyPot as a guest?',
  answer: 'Yes. Guest mode lets you try KittyPot locally on your device.'
}, {
  question: 'Does guest mode sync with other people?',
  answer: 'No. Guest mode is saved locally on your device. To sync properly across users, users need an account when supported.'
}, {
  question: 'Can I use KittyPot offline?',
  answer: 'Guest mode can work locally on your device. Logged-in shared kitties may need internet to sync changes between members.'
}, {
  question: 'Is KittyPot available now?',
  answer: 'KittyPot is currently in MVP testing. Join the waitlist to hear when testing opens.'
}, {
  question: 'Will KittyPot add payments in the future?',
  answer: 'We may explore payment features in the future, but the current MVP focuses on expense tracking and manual settlement only.'
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <div className="min-h-screen bg-[#FAFAFA] font-sans selection:bg-kitty-yellow-200 selection:text-kitty-dark" data-id="element-501">
      <SEO title="FAQ | KittyPot — Shared Expense & Settlement Tracker" description="Answers about KittyPot: tracking shared expenses, splitting costs fairly, settling up, and how manual settlement works. Current MVP: expense tracking and manual settlement only." ogTitle="KittyPot FAQ" ogDescription="Track shared expenses, split costs fairly, and see the simplest way to settle up." path="/faq" jsonLd={{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    }} data-id="element-502" />
      <Navbar data-id="element-503" />
      <main className="pt-36 pb-24" data-id="element-504">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-505">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="text-center mb-14" data-id="element-506">
            <h1 className="text-4xl md:text-5xl font-black text-kitty-dark mb-4 tracking-tight" data-id="element-507">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-500 max-w-lg mx-auto" data-id="element-508">
              Everything you need to know about KittyPot. Can't find what you're
              looking for?{' '}
              <a href="/contact" className="text-kitty-yellow-600 hover:text-kitty-yellow-700 font-medium underline underline-offset-2" data-id="element-509">
                Get in touch
              </a>
              .
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="space-y-3" data-id="element-510">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-511">
                <button onClick={() => toggle(index)} className="w-full flex items-center justify-between px-6 py-5 text-left gap-4" data-id="element-512">
                  <span className="text-base font-semibold text-kitty-dark leading-snug" data-id="element-513">
                    {faq.question}
                  </span>
                  <motion.div animate={{
                rotate: openIndex === index ? 180 : 0
              }} transition={{
                duration: 0.2
              }} className="flex-shrink-0" data-id="element-514">
                    <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-515" />
                  </motion.div>
                </button>
                <AnimatePresence initial={false} data-id="element-516">
                  {openIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-517">
                      <div className="px-6 pb-5 pt-0" data-id="element-518">
                        <p className="text-gray-500 leading-relaxed text-[15px]" data-id="element-519">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </motion.div>

          {/* Bottom CTA */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="text-center mt-16 bg-gray-950 rounded-2xl p-10" data-id="element-520">
            <h2 className="text-2xl font-bold text-white mb-3" data-id="element-521">
              Still have questions?
            </h2>
            <p className="text-gray-400 mb-6 max-w-md mx-auto" data-id="element-522">
              We're happy to help. Reach out and we'll get back to you within 24
              hours.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-523">
              <a href="/contact" className="bg-kitty-yellow hover:bg-kitty-yellow-500 text-kitty-dark px-8 py-3 rounded-full font-bold transition-all active:scale-95 shadow-md hover:shadow-lg" data-id="element-524">
                Contact Us
              </a>
              <a href="mailto:support@kittypot.com" className="text-gray-400 hover:text-white font-medium transition-colors text-sm" data-id="element-525">
                or email support@kittypot.com
              </a>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer data-id="element-526" />
    </div>;
}